// ============================================================
// ANSICHTSSACHE-AGENT – Zeichenknopf und „…“
// ============================================================
// Ein Knopf, der nur aus seinem Zeichen besteht: kein Rahmen, keine
// Flaeche, erst beim Darueberfahren ein leichter Grund. So stehen
// „…“ (weitere Aktionen) und Weiterleiten im Seitenkopf, auf dem
// Blatt, in der Karte und in der Zeile — ueberall dieselbe Gestalt.
//
// Ansagen: am 23.10.2026 „den Weiterleiten-Button generell als
// normales Icon … nicht Button … bitte vereinheitlichen“, danach
// dasselbe fuer „…“, das bis dahin an Rechnung, Angebot, Brief,
// Lieferschein, Ticket, Notiz und Freigabe ein Rahmenknopf war, im
// Projektkopf dagegen schon schlicht. Gebaut war es an rund 30
// Stellen je von Hand, mit vier Hoehen und zwei Zeichengroessen.
//
// Vier Groessen, alle mit 16-Pixel-Zeichen. Die Flaeche richtet sich
// nach der Reihe, in der der Knopf steht — eine Reihe, eine Hoehe:
//   normal  36 px — Seitenkopf, Blatt, Listenzeile; gleich hoch wie
//           jeder Knopf daneben (`size="md"`/`"icon"`)
//   mittel  32 px — Kopf des Assistenten, Preiszeilen
//   klein   28 px — Kopf einer Karte, Werkzeuge an einer Nachricht
//   winzig  24 px — Zeile im Baum (DAM, Brand-Portal)
//
// `nurBeimHinzeigen` blendet den Knopf aus, bis die Zeile (`group`)
// beruehrt wird, er den Fokus hat oder sein Menue offen ist; auf
// Geraeten ohne Zeiger steht er immer da.
//
// `MehrKnopf` ist der Ausloeser fuer „…“ und die einzige Stelle,
// an der `MoreHorizontal` als Knopf steht — `zeichen-knopf.test.ts`
// haelt das fest. Beide reichen Ref und alle Eigenschaften weiter,
// weil Menue und Panel den Ausloeser per `asChild` uebernehmen.
// ============================================================

import { forwardRef } from "react";
import { MoreHorizontal } from "lucide-react";
import { cn } from "@/lib/utils";

const GROESSE = {
  normal: "h-9 w-9 rounded-lg",
  mittel: "h-8 w-8 rounded-lg",
  klein: "h-7 w-7 rounded-md",
  winzig: "h-6 w-6 rounded-md",
} as const;

export interface ZeichenKnopfProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Was der Knopf tut — Name fuer Vorleser und Hinweis beim Darueberfahren. */
  label: string;
  groesse?: keyof typeof GROESSE;
  /**
   * Eine Stufe kraeftiger: das Ziel einer Reihe (Weiterleiten),
   * nicht ihr Rest („…“).
   */
  betont?: boolean;
  /** Laeuft gerade etwas: gesperrt und blass, ohne Rad. */
  laeuft?: boolean;
  /** Erst sichtbar, wenn die Zeile (`group`) beruehrt wird. */
  nurBeimHinzeigen?: boolean;
}

export const ZeichenKnopf = forwardRef<HTMLButtonElement, ZeichenKnopfProps>(function ZeichenKnopf(
  {
    label,
    groesse = "normal",
    betont = false,
    laeuft = false,
    nurBeimHinzeigen = false,
    disabled,
    title,
    className,
    children,
    type = "button",
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={title ?? label}
      disabled={disabled || laeuft}
      aria-busy={laeuft || undefined}
      className={cn(
        "grid shrink-0 cursor-pointer place-items-center",
        GROESSE[groesse],
        betont ? "text-text-secondary" : "text-text-tertiary",
        "hover:bg-bg-tertiary hover:text-text-primary data-[state=open]:bg-bg-tertiary data-[state=open]:text-text-primary",
        "duration-fast ease-standard transition-colors",
        "focus-visible:ring-accent/40 focus-visible:ring-2 focus-visible:outline-none",
        "disabled:pointer-events-none disabled:opacity-50",
        nurBeimHinzeigen &&
          "opacity-0 transition-[opacity,color,background-color] group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100 [@media(hover:none)]:opacity-100",
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
});

/** Der Ausloeser fuer „…“ — weitere Aktionen. */
export const MehrKnopf = forwardRef<
  HTMLButtonElement,
  Omit<ZeichenKnopfProps, "label" | "betont"> & { label?: string }
>(function MehrKnopf({ label = "Weitere Aktionen", ...rest }, ref) {
  return (
    <ZeichenKnopf ref={ref} label={label} {...rest}>
      <MoreHorizontal className="h-4 w-4" aria-hidden />
    </ZeichenKnopf>
  );
});
